import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';

/**
 * The checkbox at the start of a list row or group header. Above the
 * row's whole-row "open" button, like the pickers, so ticking it never
 * opens the row.
 */
export const RowCheckbox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  className?: string;
}> = ({ checked, indeterminate = false, onChange, label, className }) => {
  const ref = useRef<HTMLInputElement>(null);
  // Not an attribute: only settable on the element.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={label}
      className={clsx(
        'relative z-10 h-3.5 w-3.5 shrink-0 cursor-pointer rounded border-line-strong bg-transparent text-accent focus:ring-accent focus:ring-offset-0',
        className
      )}
    />
  );
};

/**
 * Above a list with checked rows: how many, that a change to one changes
 * them all, and a way to clear them.
 */
/**
 * `children` are actions on the selection (message, remove), shown in
 * place of the bulk-edit hint for lists whose rows are not edited inline.
 */
export const SelectionBar: React.FC<{
  count: number;
  onClear: () => void;
  children?: React.ReactNode;
}> = ({ count, onClear, children }) =>
  count === 0 ? null : (
    <div
      role="status"
      className="flex items-center gap-3 border-b border-line bg-accent/[0.06] px-4 py-1.5 text-[12px] text-fg-secondary"
    >
      <span className="font-medium text-fg">{count} selected</span>
      {children}
      {!children && count > 1 && (
        <span className="hidden text-fg-muted sm:inline">
          Changing a field on any of them changes it on all {count}.
        </span>
      )}
      <button
        type="button"
        onClick={onClear}
        className="ml-auto rounded px-1.5 py-0.5 text-fg-muted hover:bg-fg/5 hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        Clear
      </button>
    </div>
  );
